import React, { useState, useEffect } from 'react';
import { useSearchParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, Button, Alert, CircularProgress, Link,
} from '@mui/material';
import { verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { auth } from '../firebase';

/**
 * Our own password-reset handler, so the link in the email points at
 * batchfather.app rather than the project's firebaseapp.com action URL.
 *
 * Firebase's hosted handler is only reachable via the project-level "action
 * URL" console setting, which this project cannot save. Handling the `oobCode`
 * here removes that dependency entirely and lets the user finish on a page that
 * looks like the rest of the app.
 */
export const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const oobCode = searchParams.get('oobCode') || '';

  // Verify the code before showing the form — an expired link should say so
  // rather than letting someone type a new password and fail at the end.
  const [verifying, setVerifying] = useState(true);
  const [email, setEmail] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!oobCode) {
      setCodeError('This link is missing its reset code. Please request a new password reset.');
      setVerifying(false);
      return;
    }
    let cancelled = false;
    verifyPasswordResetCode(auth, oobCode)
      .then((verifiedEmail) => {
        if (cancelled) return;
        setEmail(verifiedEmail);
        setVerifying(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setCodeError(
          err?.code === 'auth/expired-action-code'
            ? 'This reset link has expired. Please request a new one.'
            : 'This reset link is invalid or has already been used. Please request a new one.'
        );
        setVerifying(false);
      });
    return () => { cancelled = true; };
  }, [oobCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setDone(true);
    } catch (err: any) {
      switch (err?.code) {
        case 'auth/weak-password':
          setError('That password is too weak. Use at least 6 characters.');
          break;
        case 'auth/expired-action-code':
          setError('This reset link has expired. Please request a new one.');
          break;
        case 'auth/invalid-action-code':
          setError('This reset link is no longer valid. Please request a new one.');
          break;
        default:
          setError('Could not reset your password. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 2 }}>
      <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: '100%' }}>
        <Typography variant="h5" component="h1" gutterBottom align="center">
          Choose a new password
        </Typography>

        {verifying && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {!verifying && codeError && (
          <Box>
            <Alert severity="error" sx={{ mt: 2 }}>{codeError}</Alert>
            <Button
              component={RouterLink}
              to="/forgot-password"
              variant="contained"
              fullWidth
              sx={{ mt: 3 }}
            >
              Request a new link
            </Button>
          </Box>
        )}

        {!verifying && !codeError && done && (
          <Box>
            <Alert severity="success" sx={{ mt: 2 }}>
              Your password has been changed.
            </Alert>
            <Button
              variant="contained"
              fullWidth
              sx={{ mt: 3 }}
              onClick={() => navigate('/login')}
            >
              Sign in
            </Button>
          </Box>
        )}

        {!verifying && !codeError && !done && (
          <form onSubmit={handleSubmit}>
            {email && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                for <strong>{email}</strong>
              </Typography>
            )}
            <TextField
              fullWidth
              label="New password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              autoComplete="new-password"
            />
            <TextField
              fullWidth
              label="Confirm new password"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              margin="normal"
              required
              autoComplete="new-password"
            />
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            <Button
              fullWidth
              variant="contained"
              color="primary"
              type="submit"
              disabled={saving || !password || !confirm}
              sx={{ mt: 3 }}
            >
              {saving ? <CircularProgress size={24} /> : 'Set new password'}
            </Button>
          </form>
        )}

        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Link component={RouterLink} to="/login" variant="body2">
            Back to Sign In
          </Link>
        </Box>
      </Paper>
    </Box>
  );
};

export default ResetPassword;
